import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css'
import type { Swiper as SwiperClass } from 'swiper/types'

import * as s from './ProductSlider.module.scss'
import { Pagination } from 'swiper'
import ProductItem from '../ProductItem'
import { itemData } from '../types'
import { useDynamicSlidesOffset } from '../../../hooks/useDynamicSlidesOffset'
import DashboardBlock from '../DashboardBlock'

interface ProductSliderProps {
  variant: 'product'
  data: itemData[]
}

type CommonSliderProps = {
  className?: string
  slideWidth?: number | string
  slideGap?: number
}

type IProps = ProductSliderProps & CommonSliderProps

const ProductSlider: React.FC<IProps> = ({
  data,
  slideWidth = 600,
  slideGap = 20,
}) => {
  const swiperRef = useRef<SwiperClass | null>(null)
  const outerRef = useRef<HTMLDivElement>(null)
  const stickyRef = useRef<HTMLDivElement>(null)
  const initialScrollYRef = useRef<number | null>(null)

  const [activeIndex, setActiveIndex] = useState(0)
  const [isSticky, setIsSticky] = useState(false)
  const slidesOffsetAfter = useDynamicSlidesOffset(outerRef)
  const offset = slidesOffsetAfter
  const [translateX, setTranslateX] = useState(0)
  const stickyTopOffset = 80

  const [isFirstRender, setIsFirstRender] = useState(true)

  useLayoutEffect(() => {
    if (offset !== null) {
      if (isFirstRender) {
        setIsFirstRender(false)
      } else {
        setTranslateX(offset)
      }
    }
  }, [offset, isFirstRender])

  useEffect(() => {
    const handleScroll = () => {
      const outer = outerRef.current
      const swiper = swiperRef.current
      const sticky = stickyRef.current
      if (!outer || !swiper || !sticky || !swiper.setTranslate) return

      const scrollTop = window.scrollY

      const stickyTop = sticky.getBoundingClientRect().top
      const stickyActive = stickyTop <= stickyTopOffset

      setIsSticky(stickyActive)

      if (stickyActive && initialScrollYRef.current === null) {
        initialScrollYRef.current = scrollTop
      }

      if (initialScrollYRef.current !== null) {
        const height = sticky.offsetHeight - stickyTopOffset

        const scrollProgress = (scrollTop - initialScrollYRef.current) / height

        const clampedProgress = Math.max(0, Math.min(1, scrollProgress))

        const totalSlidesWidth =
          data.length * Number(slideWidth) + (data.length - 1) * (slideGap || 0)

        const totalScrollableWidth =
          totalSlidesWidth - window.innerWidth + offset * 2

        const newTranslateX = -clampedProgress * totalScrollableWidth + offset

        if (stickyActive) {
          setTranslateX(newTranslateX)
        }

        const index = Math.round(clampedProgress * (data.length - 1))
        setActiveIndex(index)
      }

      if (!stickyActive && initialScrollYRef.current !== null) {
        initialScrollYRef.current = null

        setTranslateX(offset)
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [data, slideWidth, slideGap, offset])

  return (
    <div ref={outerRef} className={s.scrollTrack}>
      <div ref={stickyRef} className={s.stickyWrapper}>
        <div className={s.paginationWrapper}>
          {data.map((_, idx) => (
            <span
              key={idx}
              className={`${s.dot} ${idx === activeIndex ? s.active : ''}`}
            />
          ))}
        </div>
        <div className={s.swiperContainer}>
          <Swiper
            slidesPerView="auto"
            spaceBetween={slideGap}
            onSwiper={swiper => {
              swiperRef.current = swiper
              swiper.allowTouchMove = false
            }}
            modules={[Pagination]}
            style={{
              transform: `translateX(${translateX}px)`,
              transition: isSticky ? 'transform 0.1s ease-out' : 'none',
              width: 'auto',
              minWidth: '100%',
              overflow: 'visible',
            }}
          >
            {data.map((item, idx) => (
              <SwiperSlide key={idx} style={{ width: `${slideWidth}px` }}>
                <ProductItem
                  moduleName={item.moduleName}
                  text={item.text}
                  subtitle={item.subtitle}
                  image={item.image}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
        <DashboardBlock />
      </div>
    </div>
  )
}

export default ProductSlider
